import {
  HStack,
  IconButton,
  TableColumnHeaderProps,
  Text,
  Th,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { RiCloseLine } from "react-icons/ri";
import { db, Phrase } from "../db";
import ConfirmDialog from "../dialogs/ConfirmDialog";

interface PhraseHeaderProps extends TableColumnHeaderProps {
  phrase: Phrase;
  updatePhrases: () => void;
}

const PhraseHeader = ({
  phrase,
  updatePhrases,
  ...props
}: PhraseHeaderProps) => {
  const deletePhraseDisclosure = useDisclosure();

  const deletePhrase = useCallback(
    async (phrase: Phrase) => {
      try {
        await db.phrases.delete(phrase.id!);
        const audioFilesToDelete = await db.audioFiles
          .filter((f) => f.phrase === phrase.text)
          .primaryKeys();
        await db.audioFiles.bulkDelete(audioFilesToDelete);
      } catch (err) {
        console.error(
          `Failed to delete phrase and associated audio files: ${err}`
        );
      }
      await updatePhrases();
    },
    [updatePhrases]
  );

  return (
    <>
      <Th
        px={2}
        py={3}
        textTransform="none"
        textAlign="center"
        fontSize="md"
        bgColor="gray.50"
        {...props}
      >
        <HStack width="100%">
          <Tooltip label="Delete phrase" placement="top" hasArrow={true}>
            <IconButton
              colorScheme="red"
              size="xs"
              variant="ghost"
              onClick={deletePhraseDisclosure.onOpen}
              aria-label="Delete phrase"
              icon={<RiCloseLine />}
            />
          </Tooltip>
          <Text textAlign="left">{phrase.text}</Text>
        </HStack>
      </Th>
      {deletePhraseDisclosure.isOpen && (
        <ConfirmDialog
          isOpen={deletePhraseDisclosure.isOpen}
          onClose={deletePhraseDisclosure.onClose}
          title={`Delete phrase (${phrase.text})`}
          message='Deleting this phrase will also delete all associated audio data. Please click "confirm" to continue.'
          onConfirm={() => {
            deletePhrase(phrase);
            deletePhraseDisclosure.onClose();
          }}
        />
      )}
    </>
  );
};

export default PhraseHeader;
